import React, { useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { Badge } from 'primereact/badge';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { useCart } from '../../hooks/useCart';
import ProductImage from './ProductImage';
import orderService from '../../services/orderService';

const OrderSummary = ({ showProducts = true, compact = false, extraFee = 0, extraFeeLabel = 'Recargo por método de pago' }) => {
    const cart = useCart();

    // Mapear datos del carrito al esquema usado en el resumen
    const items = cart?.items || [];
    const totalItems = cart?.itemCount || 0;
    const subtotal = cart?.total || 0;

    // Configuración de envío desde variables de entorno
    const freeShippingThreshold = parseFloat(process.env.REACT_APP_FREE_SHIPPING_THRESHOLD || '500');
    const shippingCost = parseFloat(process.env.REACT_APP_SHIPPING_COST || '15');
    const shipping = subtotal >= freeShippingThreshold ? 0 : shippingCost;

    // IGV estimado incluido en precios
    const taxes = useMemo(() => subtotal * 0.18 / 1.18, [subtotal]);

    // Cupón
    const [couponCode, setCouponCode] = useState('');
    const [coupon, setCoupon] = useState(null); // { code, discountAmount, discountPercent }
    const discountAmount = useMemo(() => {
        if (!coupon) return 0;
        if (coupon.discountAmount) return coupon.discountAmount;
        if (coupon.discountPercent) return Math.round(subtotal * (coupon.discountPercent / 100));
        return 0;
    }, [coupon, subtotal]);

    const formatPrice = (price) => {
        return new Intl.NumberFormat('es-PE', {
            style: 'currency',
            currency: 'PEN'
        }).format(Number.isFinite(price) ? price : 0);
    };

    const calculateDiscount = (originalPrice, salePrice) => {
        if (!originalPrice || !salePrice || originalPrice <= salePrice) return 0;
        return Math.round(((originalPrice - salePrice) / originalPrice) * 100);
    };

    const resolveItemField = (item, field) => {
        switch (field) {
            case 'name':
                return item.nombre || item.name || 'Producto';
            case 'image':
                return item.imagen || item.imagen_principal || item.image || '/productos/placeholder.png';
            case 'salePrice':
                return item.precio_venta ?? item.salePrice ?? item.precio ?? 0;
            case 'originalPrice':
                return item.precio_lista ?? item.originalPrice ?? null;
            case 'quantity':
                return item.cantidad ?? item.quantity ?? 1;
            default:
                return item[field];
        }
    };

    const handleApplyCoupon = async () => {
        try {
            if (!couponCode) return;
            // Intentar validar cupón contra el backend
            const resp = await orderService.validateCoupon(couponCode, subtotal);
            if (resp?.success) {
                const { discountAmount, discountPercent } = resp;
                setCoupon({ code: couponCode, discountAmount, discountPercent });
            } else {
                // Soporte básico: códigos DEMO para pruebas si backend no disponible
                if (couponCode.toUpperCase() === 'DEMO5') {
                    setCoupon({ code: couponCode, discountPercent: 5 });
                } else if (couponCode.toUpperCase() === 'ENVIOFREE') {
                    // Representar envío gratis aplicando descuento equivalente al shipping actual
                    setCoupon({ code: couponCode, discountAmount: shipping });
                } else {
                    setCoupon(null);
                }
            }
        } catch (e) {
            // Fallback para entorno sin API
            if (couponCode.toUpperCase() === 'DEMO5') {
                setCoupon({ code: couponCode, discountPercent: 5 });
            } else if (couponCode.toUpperCase() === 'ENVIOFREE') {
                setCoupon({ code: couponCode, discountAmount: shipping });
            } else {
                setCoupon(null);
            }
        }
    };

    if (items.length === 0) {
        return (
            <Card className="text-center p-4">
                <div className="flex flex-column align-items-center gap-3">
                    <i className="pi pi-shopping-cart text-4xl text-color-secondary"></i>
                    <p className="text-color-secondary">No hay productos en tu carrito</p>
                </div>
            </Card>
        );
    }

    const totalBeforeAdjustments = subtotal + shipping;
    const totalWithFee = totalBeforeAdjustments + (extraFee || 0);
    const totalFinal = Math.max(0, totalWithFee - discountAmount);

    return (
        <Card className="order-summary">
            <div className="flex align-items-center justify-content-between mb-4">
                <h3 className="text-xl font-semibold m-0">Resumen del Pedido</h3>
                <Badge value={totalItems} severity="info" />
            </div>

            {showProducts && (
                <div className="mb-4">
                    <h4 className="text-lg font-medium mb-3">Productos</h4>
                    <div className="max-h-20rem overflow-auto">
                        {items.map((item) => {
                            const name = resolveItemField(item, 'name');
                            const image = resolveItemField(item, 'image');
                            const salePrice = resolveItemField(item, 'salePrice');
                            const originalPrice = resolveItemField(item, 'originalPrice');
                            const quantity = resolveItemField(item, 'quantity');
                            return (
                                <div key={item.id} className="flex align-items-center gap-3 mb-3 p-2 border-round surface-border border-1">
                                    {!compact && (
                                        <ProductImage src={image} alt={name} className="w-4rem h-4rem object-fit-cover border-round flex-shrink-0" />
                                    )}

                                    <div className="flex-1">
                                        <h5 className="text-sm font-semibold mb-1 line-height-3">{name}</h5>
                                        <div className="flex align-items-center gap-2">
                                            <span className="text-sm font-bold text-primary">
                                                {formatPrice(salePrice)}
                                            </span>
                                            {originalPrice && originalPrice > salePrice && (
                                                <>
                                                    <span className="text-xs text-color-secondary line-through">
                                                        {formatPrice(originalPrice)}
                                                    </span>
                                                    <Badge value={`-${calculateDiscount(originalPrice, salePrice)}%`} severity="success" size="small" />
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div className="text-right">
                                        <p className="text-xs text-color-secondary mb-1">Cantidad: {quantity}</p>
                                        <p className="text-sm font-bold">
                                            {formatPrice(salePrice * quantity)}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <Divider />

            <div className="order-totals">
                <div className="flex justify-content-between mb-2">
                    <span>Subtotal ({totalItems} {totalItems === 1 ? 'producto' : 'productos'}):</span>
                    <span>{formatPrice(subtotal)}</span>
                </div>

                <div className="flex justify-content-between mb-2">
                    <span>Envío:</span>
                    <span className={shipping === 0 ? 'text-green-600 font-medium' : ''}>
                        {shipping === 0 ? 'Gratis' : formatPrice(shipping)}
                    </span>
                </div>

                {/* IGV informativo (incluido en precios) */}
                <div className="flex justify-content-between mb-2">
                    <span>IGV estimado (incluido):</span>
                    <span>{formatPrice(taxes)}</span>
                </div>

                {extraFee > 0 && (
                    <div className="flex justify-content-between mb-2">
                        <span>{extraFeeLabel}:</span>
                        <span>{formatPrice(extraFee)}</span>
                    </div>
                )}

                {discountAmount > 0 && (
                    <div className="flex justify-content-between mb-2">
                        <span>Descuento:</span>
                        <span className="text-green-600">- {formatPrice(discountAmount)}</span>
                    </div>
                )}

                <Divider />

                <div className="flex justify-content-between align-items-center text-lg font-bold">
                    <span>Total:</span>
                    <span className="text-primary text-xl">{formatPrice(totalFinal)}</span>
                </div>
            </div>

            {/* Cupón de descuento */}
            {!compact && (
                <div className="mt-4">
                    <label htmlFor="coupon" className="block text-sm font-medium mb-2">
                        Código de descuento
                    </label>
                    <div className="flex gap-2">
                        <InputText id="coupon" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} placeholder="Ingresa tu código" className="flex-1" />
                        <Button label="Aplicar" onClick={handleApplyCoupon} outlined size="small" disabled={!couponCode} />
                    </div>
                </div>
            )}

            {!compact && (
                <div className="mt-4 p-3 bg-blue-50 border-round">
                    <div className="flex align-items-center gap-2 mb-2">
                        <i className="pi pi-info-circle text-blue-600"></i>
                        <span className="text-sm font-medium text-blue-800">Información de Envío</span>
                    </div>
                    <ul className="text-xs text-blue-700 m-0 pl-3">
                        <li>Envío gratis en compras mayores a S/2000</li>
                        <li>Entrega en 24-48 horas en Lima</li>
                        <li>Entrega en 3-5 días en provincias</li>
                    </ul>
                </div>
            )}

            {!compact && (
                <div className="mt-3 p-3 bg-green-50 border-round">
                    <div className="flex align-items-center gap-2 mb-2">
                        <i className="pi pi-shield text-green-600"></i>
                        <span className="text-sm font-medium text-green-800">Compra Segura</span>
                    </div>
                    <div className="flex align-items-center gap-3 text-xs text-green-700">
                        <span>SSL Certificado</span>
                        <span>•</span>
                        <span>Garantía de Devolución</span>
                        <span>•</span>
                        <span>Soporte 24/7</span>
                    </div>
                </div>
            )}
        </Card>
    );
};

export default OrderSummary;
